/**
 * HealerSchedules — Sprint 13-S9
 * Admin view of healer monthly schedules.
 * Route: /schedules — allowedRoles: admin, owner.
 *
 * Patient lastName never shown — firstName + condition only (admin privacy rule).
 * No amount shown anywhere in this view.
 * Data: GET /api/healer/calendar?month=YYYY-MM&healerId=ObjectId
 */

import React, { useState, useEffect, useCallback } from 'react';
import Sidebar   from '../components/Sidebar';
import { getHealers, getHealerCalendar } from '../utils/api';
import {
  FiCalendar, FiChevronLeft, FiChevronRight,
  FiUser, FiRefreshCw, FiPrinter,
} from 'react-icons/fi';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_HEADERS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function toYYYYMM(year, month) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function buildDayMap(days = []) {
  return days.reduce((acc, d) => { acc[d.date] = d; return acc; }, {});
}

export default function HealerSchedules() {
  const now = new Date();
  const [viewYear,  setViewYear]  = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());  // 0-indexed
  const [healers,   setHealers]   = useState([]);
  const [healerId,  setHealerId]  = useState('');
  const [calData,   setCalData]   = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res  = await getHealers();
        const list = Array.isArray(res.data) ? res.data
          : (res.data.data || res.data.healers || []);
        setHealers(list);
        if (list.length > 0) setHealerId(list[0]._id);
      } catch {
        setError('Could not load healers.');
      }
    })();
  }, []);

  const loadCalendar = useCallback(async () => {
    if (!healerId) return;
    setLoading(true);
    setError(null);
    setDayDetail(null);
    try {
      const res = await getHealerCalendar(toYYYYMM(viewYear, viewMonth), healerId);
      setCalData(res.data.data || res.data);
    } catch {
      setError('Could not load schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [healerId, viewYear, viewMonth]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  const dayMap    = buildDayMap(calData?.days);
  const firstDow  = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMo  = new Date(viewYear, viewMonth + 1, 0).getDate();
  // Local date string — avoids UTC offset causing today to appear as yesterday for IST users after 18:30
  const todayStr  = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const days = calData?.days || [];
  const summary = calData?.summary || {
    total:     days.reduce((a, d) => a + (d.available || 0) + (d.booked || 0), 0),
    booked:    days.reduce((a, d) => a + (d.booked || 0), 0),
    available: days.reduce((a, d) => a + (d.available || 0), 0),
    blocked:   days.filter(d => d.blocked).length,
  };

  function dayBg(d, isPast) {
    if (isPast)       return s.dayPast;
    if (!d)           return s.dayEmpty;
    if (d.blocked)    return s.dayBlocked;
    if (d.booked > 0) return s.dayBooked;
    if (d.available > 0) return s.dayAvailable;
    return s.dayEmpty;
  }

  return (
    <div style={s.wrap}>
      <Sidebar />
      <main style={s.main}>

        {/* Header */}
        <div style={s.header}>
          <h1 style={s.heading}>
            <FiCalendar style={{ marginRight: 10 }} />Healer Schedules
          </h1>
          <button onClick={() => window.print()} style={s.exportBtn}>
            <FiPrinter style={{ marginRight: 6 }} />Export
          </button>
        </div>

        {/* Controls */}
        <div style={s.controls}>
          <select
            value={healerId}
            onChange={e => setHealerId(e.target.value)}
            style={s.select}
          >
            {healers.length === 0 && <option value="">Loading healers...</option>}
            {healers.map(h => (
              <option key={h._id} value={h._id}>
                {h.firstName} {h.lastName}
              </option>
            ))}
          </select>

          <div style={s.monthNav}>
            <button onClick={prevMonth} style={s.navBtn} aria-label="Previous month">
              <FiChevronLeft />
            </button>
            <span style={s.monthLabel}>{MONTH_NAMES[viewMonth]} {viewYear}</span>
            <button onClick={nextMonth} style={s.navBtn} aria-label="Next month">
              <FiChevronRight />
            </button>
          </div>

          <button onClick={loadCalendar} style={s.refreshBtn} disabled={loading}>
            <FiRefreshCw style={{ marginRight: 6, opacity: loading ? 0.5 : 1 }} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        {/* Summary row */}
        {calData && (
          <div style={s.summaryRow}>
            {[
              { label: 'Total slots', value: summary.total,     color: '#1a3d2b' },
              { label: 'Booked',      value: summary.booked,    color: '#0D7377' },
              { label: 'Available',   value: summary.available, color: '#16a34a' },
              { label: 'Blocked',     value: summary.blocked,   color: '#dc2626' },
            ].map(stat => (
              <div key={stat.label} style={s.statBox}>
                <span style={{ ...s.statVal, color: stat.color }}>{stat.value}</span>
                <span style={s.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Calendar grid */}
        <div style={s.calCard}>
          <div style={s.calGrid}>
            {DAY_HEADERS.map(d => (
              <div key={d} style={s.dayHeader}>{d}</div>
            ))}
            {Array.from({ length: firstDow }, (_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMo }, (_, i) => {
              const day    = i + 1;
              const date   = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const d      = dayMap[date];
              const isPast = date < todayStr;
              return (
                <div
                  key={date}
                  style={{ ...s.dayCell, ...dayBg(d, isPast), cursor: d && !isPast ? 'pointer' : 'default' }}
                  onClick={() => d && !isPast && setDayDetail({ date, ...d })}
                  title={d ? `${d.available || 0} available · ${d.booked || 0} booked` : undefined}
                >
                  <span style={s.dayNum}>{day}</span>
                  {d?.booked > 0    && <span style={s.badgeBooked}>{d.booked} booked</span>}
                  {d?.available > 0 && <span style={s.badgeAvail}>{d.available} open</span>}
                  {d?.blocked       && <span style={s.badgeBlocked}>blocked</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div style={s.legend}>
          {[
            { bg: '#d1fae5', border: '#16a34a', label: 'Available' },
            { bg: '#ccfbf1', border: '#0D7377', label: 'Booked' },
            { bg: '#fee2e2', border: '#dc2626', label: 'Blocked' },
            { bg: '#f3f4f6', border: '#d1d5db', label: 'Past / empty' },
          ].map(l => (
            <span key={l.label} style={s.legendItem}>
              <span style={{ ...s.legendDot, background: l.bg, border: `1.5px solid ${l.border}` }} />
              {l.label}
            </span>
          ))}
        </div>

        {/* Day detail modal */}
        {dayDetail && (
          <div style={s.overlay} onClick={() => setDayDetail(null)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={s.modalHeader}>
                <span style={s.modalTitle}>
                  <FiCalendar style={{ marginRight: 8 }} />
                  {new Date(dayDetail.date + 'T00:00:00').toLocaleDateString('en-IN',
                    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <button style={s.closeBtn} onClick={() => setDayDetail(null)} aria-label="Close">
                  ×
                </button>
              </div>

              {(!dayDetail.sessions || dayDetail.sessions.length === 0) ? (
                <p style={s.modalEmpty}>No sessions recorded for this day.</p>
              ) : dayDetail.sessions.map((sess, i) => (
                <div key={i} style={s.sessRow}>
                  <FiUser style={s.sessIcon} />
                  <div>
                    {/* Patient lastName never shown — admin privacy rule */}
                    <div style={s.sessName}>{sess.patientFirstName}</div>
                    <div style={s.sessMeta}>{sess.time} · {sess.sessionType}</div>
                    {sess.condition && (
                      <div style={s.sessCond}>{sess.condition}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

const s = {
  wrap:     { display: 'flex', minHeight: '100vh', background: '#f8fafb' },
  main:     { marginLeft: 260, flex: 1, padding: '32px 28px', fontFamily: "'Open Sans', sans-serif" },

  header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  heading:   { fontSize: 22, fontWeight: 700, color: '#1a3d2b', display: 'flex', alignItems: 'center', margin: 0 },
  exportBtn: { display: 'flex', alignItems: 'center', padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' },

  controls:  { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
  select:    { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, minWidth: 200, background: '#fff' },
  monthNav:  { display: 'flex', alignItems: 'center', gap: 8 },
  navBtn:    { background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', color: '#374151' },
  monthLabel:{ fontSize: 15, fontWeight: 700, color: '#1a3d2b', minWidth: 150, textAlign: 'center' },
  refreshBtn:{ display: 'flex', alignItems: 'center', padding: '8px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' },

  errorBox:  { background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14 },

  summaryRow:{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  statBox:   { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110 },
  statVal:   { fontSize: 24, fontWeight: 700, marginBottom: 2 },
  statLabel: { fontSize: 12, color: '#6b7280' },

  calCard:   { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20, marginBottom: 16 },
  calGrid:   { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
  dayHeader: { textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#9ca3af', padding: '6px 0', textTransform: 'uppercase', letterSpacing: 0.5 },
  dayCell:   { minHeight: 72, borderRadius: 8, padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 2, border: '1.5px solid #e5e7eb' },
  dayNum:    { fontSize: 13, fontWeight: 600, color: '#374151' },
  dayEmpty:  { background: '#fff', borderColor: '#e5e7eb' },
  dayPast:   { background: '#f3f4f6', borderColor: '#e5e7eb', opacity: 0.55 },
  dayBooked: { background: '#ccfbf1', borderColor: '#0D7377' },
  dayAvailable: { background: '#d1fae5', borderColor: '#16a34a' },
  dayBlocked:{ background: '#fee2e2', borderColor: '#dc2626' },
  badgeBooked:  { fontSize: 10, fontWeight: 600, color: '#0D7377',  background: '#ccfbf1', borderRadius: 4, padding: '1px 4px', whiteSpace: 'nowrap' },
  badgeAvail:   { fontSize: 10, fontWeight: 600, color: '#16a34a',  background: '#d1fae5', borderRadius: 4, padding: '1px 4px', whiteSpace: 'nowrap' },
  badgeBlocked: { fontSize: 10, fontWeight: 600, color: '#dc2626',  background: '#fee2e2', borderRadius: 4, padding: '1px 4px', whiteSpace: 'nowrap' },

  legend:    { display: 'flex', gap: 20, fontSize: 12, color: '#6b7280', flexWrap: 'wrap', marginTop: 4 },
  legendItem:{ display: 'flex', alignItems: 'center', gap: 6 },
  legendDot: { width: 14, height: 14, borderRadius: 4, display: 'inline-block', flexShrink: 0 },

  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:    { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 440, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  modalTitle:  { fontSize: 15, fontWeight: 700, color: '#1a3d2b', display: 'flex', alignItems: 'center', flex: 1 },
  closeBtn:    { background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 0 0 8px' },
  modalEmpty:  { color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: '20px 0', margin: 0 },
  sessRow:  { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f3f4f6' },
  sessIcon: { color: '#0D7377', fontSize: 18, marginTop: 2, flexShrink: 0 },
  sessName: { fontSize: 14, fontWeight: 600, color: '#1a3d2b' },
  sessMeta: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  sessCond: { fontSize: 12, color: '#374151', marginTop: 2, fontStyle: 'italic' },
};
